/**
 * 補足説明文のツールチップ。
 *
 * 各項目の補足説明を常時表示すると画面が説明文で埋まり、入力欄を探しにくくなる。
 * 説明文は「ⓘ」アイコンにマウスを乗せる (またはキーボードでフォーカスする) と、
 * マウスカーソルに追従するツールチップとして表示する。
 *
 * - ブラウザ標準の `title` は表示までの遅延が大きく、スタイルも付けられず、
 *   キーボード操作では出ないため使わない。
 * - 説明文は `aria-describedby` で ⓘ ボタンに紐づけ、スクリーンリーダーでも読める。
 * - 画面端ではみ出さないよう、表示位置は viewport 内に収まるよう補正する。
 * - Esc キーで閉じられる。
 *
 * 使い分け: **エラー・警告・現在の状態を伝える文は非表示にしてはいけない**
 * (例: 言語の上限到達、チャイム未登録の案内、アップロード中)。
 * 本部品は「操作前に読めばよい補足説明」だけに使う。
 */
import { useId, useState, type MouseEvent } from 'react';

/** マウスカーソルからツールチップ左上までのずらし量 (px)。カーソルに重ならないようにする。 */
const CURSOR_OFFSET = 14;
/** ツールチップの最大幅 (px)。styles.css の `.hint-tooltip__bubble` と揃える。 */
const BUBBLE_MAX_WIDTH = 320;
/** 画面端との最小余白 (px) */
const EDGE_MARGIN = 8;

interface Position {
  x: number;
  y: number;
}

/** viewport からはみ出さない座標に補正する。 */
function clampToViewport(x: number, y: number): Position {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - BUBBLE_MAX_WIDTH - EDGE_MARGIN);
  return {
    x: Math.min(Math.max(EDGE_MARGIN, x), maxX),
    y: Math.max(EDGE_MARGIN, y),
  };
}

export function HintTooltip({
  children,
  label = '補足説明',
  testId,
}: {
  /** 説明文 (ホバー / フォーカス時に表示する) */
  children: React.ReactNode;
  /** ⓘ ボタンの aria-label。何についての説明か分かる名前を渡す (例: 「放送遅延の説明」)。 */
  label?: string;
  /** E2E / テスト用の data-testid。説明文本体にも `${testId}-text` を付ける。 */
  testId?: string;
}): React.JSX.Element {
  const id = useId();
  const [position, setPosition] = useState<Position | null>(null);

  const showAtCursor = (event: MouseEvent<HTMLElement>): void => {
    setPosition(clampToViewport(event.clientX + CURSOR_OFFSET, event.clientY + CURSOR_OFFSET));
  };

  const showAtElement = (element: HTMLElement): void => {
    const rect = element.getBoundingClientRect();
    setPosition(clampToViewport(rect.left, rect.bottom + 4));
  };

  return (
    <span className="hint-tooltip" data-testid={testId}>
      <button
        type="button"
        className="hint-tooltip__trigger"
        aria-label={label}
        aria-describedby={position ? id : undefined}
        onMouseEnter={showAtCursor}
        // マウスカーソルと一緒に動かす。
        onMouseMove={showAtCursor}
        onMouseLeave={() => setPosition(null)}
        onFocus={(event) => showAtElement(event.currentTarget)}
        onBlur={() => setPosition(null)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setPosition(null);
        }}
      >
        ⓘ
      </button>
      {position ? (
        <span
          id={id}
          role="tooltip"
          className="hint-tooltip__bubble"
          data-testid={testId ? `${testId}-text` : undefined}
          style={{ left: position.x, top: position.y }}
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}
